<script setup>
import { computed, onBeforeUnmount, reactive, ref } from 'vue';
import ContactsAPI from 'dashboard/api/contacts';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import InputGroup from 'dashboard/components-next/input/InputGroup.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import {
  formatCpf,
  formatCnpj,
  onlyDigits,
} from 'shared/helpers/talkviaInputMasks';

const props = defineProps({
  saving: { type: Boolean, default: false },
  error: { type: String, default: '' },
  // Dados bancários são do Financeiro: quem só gerencia estoque não vê nem envia.
  showBank: { type: Boolean, default: true },
});
const emit = defineEmits(['save']);
const dialog = ref();
const supplierId = ref(null);
const bankDefaults = () => ({
  bank_name: '',
  bank_code: '',
  branch: '',
  account_number: '',
  account_type: '',
  pix_key: '',
  pix_key_type: '',
  holder_name: '',
  holder_document: '',
});
const defaults = () => ({
  name: '',
  contact_id: '',
  document: '',
  email: '',
  phone: '',
  address: '',
  notes: '',
  active: true,
  bank_details: bankDefaults(),
});
const form = reactive(defaults());
const lookupBusy = ref(false);
const lookupError = ref('');
const contactError = ref('');
const contacts = ref([]);
const contactLabel = ref('');
let searchSequence = 0;
let lookupSequence = 0;
const contactOptions = computed(() =>
  contacts.value.map(contact => ({
    value: contact.id,
    label: [contact.name, contact.phone_number || contact.email]
      .filter(Boolean)
      .join(' · '),
  }))
);
const searchContacts = async (query = '') => {
  searchSequence += 1;
  const sequence = searchSequence;
  contactError.value = '';
  try {
    const response = query.trim()
      ? await ContactsAPI.search(query.trim())
      : await ContactsAPI.get(1);
    if (sequence === searchSequence)
      contacts.value = response.data.payload || [];
  } catch {
    if (sequence === searchSequence)
      contactError.value =
        'Não foi possível buscar os contatos. Tente novamente.';
  }
};
const selectContact = id => {
  const contact = contacts.value.find(item => item.id === id);
  if (!contact) return;
  contactLabel.value = contact.name;
  form.email = contact.email || form.email;
  form.phone = contact.phone_number || form.phone;
};
const lookupCnpj = async () => {
  const document = onlyDigits(form.document);
  if (document.length !== 14 || lookupBusy.value) return;
  lookupSequence += 1;
  const sequence = lookupSequence;
  lookupBusy.value = true;
  lookupError.value = '';
  try {
    const response = await fetch(
      `https://brasilapi.com.br/api/cnpj/v1/${document}`
    );
    if (!response.ok) throw new Error('lookup failed');
    const data = await response.json();
    if (sequence !== lookupSequence || document !== onlyDigits(form.document))
      return;
    form.name = form.name || data.razao_social || data.nome_fantasia || '';
    form.email = form.email || data.email || '';
    form.phone = form.phone || data.ddd_telefone_1 || '';
    form.address =
      form.address ||
      [
        data.logradouro,
        data.numero,
        data.complemento,
        data.bairro,
        data.municipio,
        data.uf,
        data.cep,
      ]
        .filter(Boolean)
        .join(', ');
  } catch {
    if (sequence === lookupSequence)
      lookupError.value =
        'Não foi possível consultar o CNPJ. Confira o número ou preencha os dados manualmente.';
  } finally {
    if (sequence === lookupSequence) lookupBusy.value = false;
  }
};
const invalidateRequests = () => {
  searchSequence += 1;
  lookupSequence += 1;
  lookupBusy.value = false;
};
onBeforeUnmount(invalidateRequests);
const documentLabel = value =>
  onlyDigits(value).length > 11 ? formatCnpj(value) : formatCpf(value);
const bankOptions = [
  { value: '', label: 'Selecione o tipo de conta' },
  { value: 'checking', label: 'Conta corrente' },
  { value: 'savings', label: 'Conta poupança' },
  { value: 'payment', label: 'Conta de pagamento' },
];
const pixOptions = [
  { value: '', label: 'Selecione o tipo de chave' },
  { value: 'cpf', label: 'CPF' },
  { value: 'cnpj', label: 'CNPJ' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
  { value: 'random', label: 'Chave aleatória' },
];
const bankConfigured = computed(() =>
  Boolean(
    form.bank_details.pix_key.trim() || form.bank_details.account_number.trim()
  )
);
const open = (supplier = {}) => {
  invalidateRequests();
  lookupError.value = '';
  contactError.value = '';
  contacts.value = [];
  contactLabel.value = supplier.contact_name || '';
  supplierId.value = supplier.id || null;
  const empty = defaults();
  Object.keys(empty)
    .filter(key => key !== 'bank_details')
    .forEach(key => {
      form[key] = supplier[key] ?? empty[key];
    });
  form.document = documentLabel(form.document);
  form.bank_details = Object.fromEntries(
    Object.keys(bankDefaults()).map(key => [
      key,
      supplier.bank_details?.[key] || '',
    ])
  );
  form.bank_details.holder_document = documentLabel(
    form.bank_details.holder_document
  );
  dialog.value.open();
};
const close = () => {
  invalidateRequests();
  dialog.value.close();
};
const submit = () => {
  if (props.saving || lookupBusy.value || !form.name.trim()) return;
  const { bank_details: bank, ...fields } = form;
  const data = {
    ...fields,
    name: form.name.trim(),
    document: onlyDigits(form.document),
    ...(props.showBank
      ? {
          bank_details: {
            ...bank,
            holder_document: onlyDigits(bank.holder_document),
          },
        }
      : {}),
  };
  emit('save', { id: supplierId.value, supplier: data });
};
defineExpose({ open, close });
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <Dialog
    ref="dialog"
    solid
    width="4xl"
    overflow-y-auto
    sticky-header-footer
    :title="supplierId ? 'Editar fornecedor' : 'Cadastrar fornecedor'"
    description="Reúna os contatos e os dados que sua equipe usa para organizar os pagamentos."
    :show-confirm-button="false"
    :show-cancel-button="false"
    :close-on-click-outside="!saving"
    @close="invalidateRequests"
    @confirm="submit"
  >
    <div class="grid min-w-0 gap-6 lg:grid-cols-2">
      <div class="flex min-w-0 flex-col gap-5">
        <section
          class="flex flex-col gap-4 rounded-xl border border-n-weak p-4"
        >
          <div class="flex items-center gap-3">
            <span class="i-lucide-building-2 size-5 shrink-0 text-n-slate-11" />
            <div>
              <h4 class="m-0 text-sm font-semibold text-n-slate-12">
                Identificação
              </h4>
              <p class="m-0 text-xs text-n-slate-11">
                Empresa ou profissional que fornece para você.
              </p>
            </div>
          </div>
          <InputGroup label="CPF ou CNPJ" for="supplier-document">
            <Input
              id="supplier-document"
              :model-value="form.document"
              placeholder="Informe o documento"
              inputmode="numeric"
              maxlength="18"
              :disabled="saving"
              @update:model-value="form.document = documentLabel($event)"
            />
            <template #append>
              <Button
                label="Buscar CNPJ"
                icon="i-hugeicons-search-01"
                variant="faded"
                color="slate"
                type="button"
                class="shrink-0"
                :disabled="
                  saving ||
                  lookupBusy ||
                  onlyDigits(form.document).length !== 14
                "
                :is-loading="lookupBusy"
                @click="lookupCnpj"
              />
            </template>
          </InputGroup>
          <Input
            v-model="form.name"
            label="Nome / razão social"
            placeholder="Como o fornecedor é conhecido"
            required
            :disabled="saving"
            maxlength="250"
          />
          <p
            v-if="lookupError"
            role="alert"
            class="mb-0 text-xs text-n-ruby-11"
          >
            {{ lookupError }}
          </p>
        </section>
        <section
          class="flex flex-col gap-4 rounded-xl border border-n-weak p-4"
        >
          <div class="flex items-center gap-3">
            <span class="i-lucide-contact size-5 shrink-0 text-n-slate-11" />
            <h4 class="m-0 text-sm font-semibold text-n-slate-12">
              Contato e endereço
            </h4>
          </div>
          <label class="mb-0 flex flex-col gap-2 text-sm text-n-slate-12">
            Contato do sistema
            <ComboBox
              v-model="form.contact_id"
              :options="contactOptions"
              :display-label="contactLabel"
              placeholder="Buscar contato existente"
              search-placeholder="Nome, telefone ou documento"
              use-api-results
              teleport-to-body
              :disabled="saving"
              @open="searchContacts()"
              @search="searchContacts"
              @update:model-value="selectContact"
            />
          </label>
          <Button
            v-if="form.contact_id"
            label="Usar outro contato ou cadastrar"
            variant="link"
            color="slate"
            type="button"
            :disabled="saving"
            @click="
              form.contact_id = '';
              contactLabel = '';
            "
          />
          <p v-else class="mb-0 text-xs text-n-slate-11">
            Ao salvar, vincularemos o contato pelos dados informados. Se não
            existir, ele será cadastrado no sistema.
          </p>
          <p
            v-if="contactError"
            role="alert"
            class="mb-0 text-xs text-n-ruby-11"
          >
            {{ contactError }}
          </p>
          <Input
            v-model="form.email"
            label="E-mail"
            type="email"
            placeholder="financeiro@empresa.com.br"
            :disabled="saving"
            maxlength="250"
          />
          <Input
            v-model="form.phone"
            label="Telefone"
            type="tel"
            placeholder="DDD e número"
            :disabled="saving"
            maxlength="30"
          />
          <Input
            v-model="form.address"
            label="Endereço completo"
            placeholder="Rua, número, bairro, cidade e CEP"
            :disabled="saving"
            maxlength="1000"
          />
          <label
            class="mb-0 flex flex-col gap-2 text-sm text-n-slate-12"
            for="supplier-notes"
          >
            Observações
            <textarea
              id="supplier-notes"
              v-model="form.notes"
              rows="3"
              maxlength="2000"
              :disabled="saving"
              placeholder="Pessoa de contato, condições combinadas ou outras orientações"
              class="mb-0 w-full resize-y rounded-lg border border-n-weak bg-transparent text-sm text-n-slate-12"
            />
          </label>
        </section>
      </div>
      <section
        v-if="showBank"
        class="flex min-w-0 flex-col gap-4 self-start rounded-xl border border-n-weak p-4"
      >
        <div class="flex items-center gap-3">
          <span class="i-lucide-landmark size-5 shrink-0 text-n-slate-11" />
          <div>
            <h4 class="m-0 text-sm font-semibold text-n-slate-12">
              Dados para pagamento
            </h4>
            <p class="m-0 text-xs text-n-slate-11">
              Preencha uma conta bancária, uma chave Pix ou ambas.
            </p>
          </div>
        </div>
        <div class="grid min-w-0 grid-cols-[minmax(0,1fr)_5.5rem] gap-3">
          <Input
            v-model="form.bank_details.bank_name"
            label="Banco"
            placeholder="Nome da instituição"
            :disabled="saving"
            maxlength="250"
          />
          <Input
            v-model="form.bank_details.bank_code"
            label="Código"
            placeholder="000"
            :disabled="saving"
            maxlength="10"
          />
        </div>
        <label class="mb-0 flex flex-col gap-2 text-sm text-n-slate-12">
          Tipo de conta
          <ComboBox
            v-model="form.bank_details.account_type"
            :options="bankOptions"
            teleport-to-body
            :disabled="saving"
            placeholder="Selecione o tipo de conta"
          />
        </label>
        <div class="grid min-w-0 grid-cols-2 gap-3">
          <Input
            v-model="form.bank_details.branch"
            label="Agência"
            placeholder="Com dígito, se houver"
            :disabled="saving"
            maxlength="30"
          />
          <Input
            v-model="form.bank_details.account_number"
            label="Conta com dígito"
            placeholder="Número da conta"
            :disabled="saving"
            maxlength="40"
          />
        </div>
        <div class="flex flex-col gap-4 border-t border-n-weak pt-4">
          <label class="mb-0 flex flex-col gap-2 text-sm text-n-slate-12">
            Tipo de chave Pix
            <ComboBox
              v-model="form.bank_details.pix_key_type"
              :options="pixOptions"
              teleport-to-body
              :disabled="saving"
              placeholder="Selecione o tipo de chave"
            />
          </label>
          <Input
            v-model="form.bank_details.pix_key"
            label="Chave Pix"
            placeholder="Digite ou cole a chave"
            :disabled="saving"
            maxlength="250"
          />
        </div>
        <div class="flex flex-col gap-4 border-t border-n-weak pt-4">
          <Input
            v-model="form.bank_details.holder_name"
            label="Nome do titular"
            placeholder="Como aparece na conta bancária"
            :disabled="saving"
            maxlength="250"
          />
          <Input
            :model-value="form.bank_details.holder_document"
            label="CPF / CNPJ do titular"
            placeholder="Documento do titular da conta"
            inputmode="numeric"
            maxlength="18"
            :disabled="saving"
            @update:model-value="
              form.bank_details.holder_document = documentLabel($event)
            "
          />
        </div>
        <p class="m-0 flex items-start gap-2 text-xs leading-5 text-n-slate-11">
          <span class="i-lucide-info mt-0.5 size-4 shrink-0" />
          Confira os dados antes de pagar. O cadastro organiza as informações e
          não realiza transferências.
        </p>
      </section>
    </div>
    <p
      v-if="error"
      role="alert"
      class="mb-0 mt-4 rounded-lg border border-n-ruby-6 p-3 text-sm text-n-ruby-11"
    >
      {{ error }}
    </p>
    <template #footer>
      <div class="flex flex-wrap items-center justify-between gap-4">
        <div class="flex min-w-0 items-center gap-3">
          <span class="i-lucide-building-2 size-5 shrink-0 text-n-slate-11" />
          <div class="min-w-0">
            <p
              class="m-0 max-w-72 truncate text-sm font-medium text-n-slate-12"
            >
              {{ form.name || 'Novo fornecedor' }}
            </p>
            <p class="m-0 text-xs text-n-slate-11">
              {{
                !showBank
                  ? 'Dados bancários ficam com o Financeiro'
                  : bankConfigured
                    ? 'Dados para pagamento preenchidos'
                    : 'Dados bancários podem ser preenchidos depois'
              }}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Button
            type="button"
            label="Cancelar"
            color="slate"
            variant="ghost"
            :disabled="saving"
            @click="close"
          />
          <Button
            type="submit"
            :label="supplierId ? 'Salvar alterações' : 'Cadastrar fornecedor'"
            icon="i-lucide-check"
            :disabled="saving || lookupBusy || !form.name.trim()"
            :is-loading="saving"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>
